import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
const generateBacktestData = () => {
  const data = [];
  let value = 10000;
  for (let i = 0; i < 100; i++) {
    value += (Math.random() - 0.45) * 200;
    data.push({
      day: i,
      equity: value,
      upper: value + Math.random() * 500,
      lower: value - Math.random() * 500
    });
  }
  return data;
};
export function ShowDontTell() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [data, setData] = useState(generateBacktestData());
  const [metrics, setMetrics] = useState({
    totalReturn: 23.4,
    maxDrawdown: -8.2,
    sharpeRatio: 1.85
  });
  // Continuously update data to make it feel alive
  useEffect(() => {
    const interval = setInterval(() => {
      setData(prevData => {
        const newData = [...prevData];
        // Update last few points
        for (let i = Math.max(0, newData.length - 5); i < newData.length; i++) {
          const baseValue = newData[i].equity;
          newData[i] = {
            ...newData[i],
            equity: baseValue + (Math.random() - 0.5) * 50,
            upper: baseValue + Math.random() * 500,
            lower: baseValue - Math.random() * 500
          };
        }
        return newData;
      });
      // Slightly animate metrics
      setMetrics({
        totalReturn: 23.4 + (Math.random() - 0.5) * 0.5,
        maxDrawdown: -8.2 + (Math.random() - 0.5) * 0.3,
        sharpeRatio: 1.85 + (Math.random() - 0.5) * 0.05
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  return <section ref={ref} className="w-full py-32 px-6 bg-navy-900" data-id="element-343">
      <div className="max-w-6xl mx-auto" data-id="element-344">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-4xl md:text-5xl font-bold mb-12 text-center text-white" data-id="element-345">
          Show, Don't Tell
        </motion.h2>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-8 hover:border-steel transition-all duration-300 shadow-lg" data-id="element-346">
          <div className="mb-6" data-id="element-347">
            <h3 className="text-2xl font-bold mb-2 text-white" data-id="element-348">
              Illustrative Backtest Simulation
            </h3>
            <p className="text-slate text-sm" data-id="element-349">
              Example mean-reversion strategy with Monte Carlo confidence bands.
              Not investment advice.
            </p>
          </div>
          <motion.div className="w-full h-80" animate={{
          opacity: [0.95, 1, 0.95]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-350">
            <ResponsiveContainer width="100%" height="100%" data-id="element-351">
              <AreaChart data={data} data-id="element-352">
                <defs data-id="element-353">
                  <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1" data-id="element-354">
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3} data-id="element-355" />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} data-id="element-356" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} data-id="element-357" />
                <XAxis dataKey="day" stroke="#CBD5E1" label={{
                value: 'Days',
                position: 'insideBottom',
                offset: -5,
                fill: '#CBD5E1'
              }} data-id="element-358" />
                <YAxis stroke="#CBD5E1" label={{
                value: 'Portfolio Value ($)',
                angle: -90,
                position: 'insideLeft',
                fill: '#CBD5E1'
              }} data-id="element-359" />
                <Tooltip contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #475569',
                borderRadius: '8px',
                color: '#CBD5E1'
              }} data-id="element-360" />
                <Area type="monotone" dataKey="upper" stroke="none" fill="#14B8A6" fillOpacity={0.1} isAnimationActive={true} animationDuration={1500} data-id="element-361" />
                <Area type="monotone" dataKey="lower" stroke="none" fill="#14B8A6" fillOpacity={0.1} isAnimationActive={true} animationDuration={1500} data-id="element-362" />
                <Line type="monotone" dataKey="equity" stroke="#60A5FA" strokeWidth={3} dot={false} isAnimationActive={true} animationDuration={1500} data-id="element-363" />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
          <div className="mt-6 grid grid-cols-3 gap-4" data-id="element-364">
            <motion.div whileHover={{
            scale: 1.05
          }} animate={{
            opacity: [0.9, 1, 0.9]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="text-center p-4 bg-navy-900 rounded-lg border border-navy-700" data-id="element-365">
              <p className="text-sm text-slate mb-1" data-id="element-366">Total Return</p>
              <motion.p key={metrics.totalReturn} initial={{
              scale: 1
            }} animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 0.3
            }} className="text-2xl font-bold text-[#14B8A6]" data-id="element-367">
                +{metrics.totalReturn.toFixed(1)}%
              </motion.p>
            </motion.div>
            <motion.div whileHover={{
            scale: 1.05
          }} animate={{
            opacity: [0.9, 1, 0.9]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.3
          }} className="text-center p-4 bg-navy-900 rounded-lg border border-navy-700" data-id="element-368">
              <p className="text-sm text-slate mb-1" data-id="element-369">Max Drawdown</p>
              <motion.p key={metrics.maxDrawdown} initial={{
              scale: 1
            }} animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 0.3
            }} className="text-2xl font-bold text-[#F59E0B]" data-id="element-370">
                {metrics.maxDrawdown.toFixed(1)}%
              </motion.p>
            </motion.div>
            <motion.div whileHover={{
            scale: 1.05
          }} animate={{
            opacity: [0.9, 1, 0.9]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.6
          }} className="text-center p-4 bg-navy-900 rounded-lg border border-navy-700" data-id="element-371">
              <p className="text-sm text-slate mb-1" data-id="element-372">Sharpe Ratio</p>
              <motion.p key={metrics.sharpeRatio} initial={{
              scale: 1
            }} animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 0.3
            }} className="text-2xl font-bold text-[#60A5FA]" data-id="element-373">
                {metrics.sharpeRatio.toFixed(2)}
              </motion.p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}